import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import InputFormField from "../../components/SharedComp/InputFormField";
import FormFieldBlock from "../../components/SharedComp/FormFieldBlock";
import { useFormik } from "formik";
import BusinessRuleCheckbox from "./BusinessRuleCheckbox";
import { makeStyles } from "@mui/styles";
import { ModalHeader, FormPanel, FormActions } from "../../components/SharedComp/DesignModal";
import {
  networkSchema,
  NAME_MAX,
  NUMBER_MAX,
  URL_MAX,
} from "../../Utils/validationSchemas";
import { DESIGN_TEXT } from "../../Utils/e5Messages";

// function to return business rule Values
const getBusinessRules = (list) => {
  let rules = [];
  rules =
    list.length > 0
      ? list.map((item, i) => {
          return item.ruleValue;
        })
      : [];
  return rules;
};

const trimValue = (v) => (typeof v === "string" ? v.trim() : v);
const sameRules = (a, b) =>
  JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

/**
//    * @component  Add Edit Network  Component
//    * @description  Add Edit Network form (New Network modal and the inline Edit Network row),
//    * following the E5 design rules, messages and saving states.
//    * @returns
//    */
const AddEditNetwork = (props) => {
  const [updateNetworkData] = useState(props.data ? props.data : {}); // to store edit network details
  const isEdit = !!(updateNetworkData && updateNetworkData.id);
  const saving = !!(props.loaderSave || props.dialogLoader);
  const [NoChange, setNoChange] = useState(false);
  // errors from the last Save (409 / 400), cleared per field as the user edits it
  const [serverErrors, setServerErrors] = useState({});
  // form-level message beside Save (mapping duplicate, or "Your changes were not saved.")
  const [formTopError, setFormTopError] = useState("");

  const initialRules =
    updateNetworkData &&
    updateNetworkData.businessRule &&
    updateNetworkData.businessRule.rules &&
    updateNetworkData.businessRule.rules.length > 0
      ? getBusinessRules(updateNetworkData.businessRule.rules)
      : ["Fill", "Bnus", "Paid"];
  // rules as currently ticked in the checkbox component (used to detect changes)
  const [BusinessRuleRef, setBusinessRuleRef] = useState(initialRules);

  // DOM refs, used to focus the first invalid field
  const networkNameInput = useRef(null);
  const networkNumberInput = useRef(null);
  const mediaFieBasePathInput = useRef(null);
  const businessRuleInput = useRef(null);

  const parentZoneLabel = props.zone
    ? `${props.zone.zoneName} / ${props.zone.zoneNumber}`
    : "";

  const initialValues = {
    networkName: updateNetworkData.networkName ? updateNetworkData.networkName : "",
    networkNumber: updateNetworkData.networkNumber ? updateNetworkData.networkNumber : "",
    mediaFieBasePath: updateNetworkData.mediaFieBasePath
      ? updateNetworkData.mediaFieBasePath
      : "",
    businessRule:
      updateNetworkData.businessRule &&
      updateNetworkData.businessRule.rules &&
      updateNetworkData.businessRule.rules.length > 0
        ? getBusinessRules(updateNetworkData.businessRule.rules)
        : [],
    mznlId: updateNetworkData.mznlId ? updateNetworkData.mznlId : 0,
    id: updateNetworkData.id ? updateNetworkData.id : 0,
    createdBy: updateNetworkData.createdBy ? updateNetworkData.createdBy : 1,
  };

  /**
   * values sent to the parent: trimmed; on edit the number is always the stored one (immutable)
   */
  const toPayload = (formValues) => ({
    ...formValues,
    networkName: trimValue(formValues.networkName),
    networkNumber: isEdit
      ? initialValues.networkNumber
      : trimValue(formValues.networkNumber),
    mediaFieBasePath: trimValue(formValues.mediaFieBasePath),
  });

  /**
   * submit: the parent returns {ok, fieldErrors, formError}; show failures in the form
   */
  // one save request at a time, even if Save is hit again before the parent shows "Saving…"
  const submittingRef = useRef(false);
  const submitToParent = async (formValues) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setFormTopError("");
    let feedback;
    try {
      feedback = await props.handleSubmit(
        toPayload(formValues),
        isEdit ? updateNetworkData.id : undefined
      );
    } finally {
      submittingRef.current = false;
    }
    // on success the form closes (component unmounts), so only apply failures
    if (feedback && !feedback.ok) {
      setServerErrors(feedback.fieldErrors || {});
      setFormTopError(feedback.formError || "");
      Object.keys(feedback.fieldErrors || {}).forEach((f) => setFieldTouched(f, true, false));
    }
  };

  /**
   * formik form state
   */
  const {
    handleSubmit,
    getFieldProps,
    values,
    setErrors,
    setFieldTouched,
    touched,
    errors,
    validateForm,
  } = useFormik({
    initialValues,
    validationSchema: networkSchema,

    onSubmit(formValues) {
      if (saving) return;
      if (isEdit && !hasChanges(formValues)) {
        setNoChange(true);
        setTimeout(function () {
          setNoChange(false);
        }, 2000);
        return;
      }
      submitToParent(formValues);
    },
  });

  /**
   * Edit: name, media URL (trimmed) or ticked rules differ from the stored network; Number is immutable.
   * Create: anything typed at all.
   */
  const hasChanges = (formValues) => {
    const current = formValues || values;
    const fields = isEdit
      ? ["networkName", "mediaFieBasePath"]
      : ["networkName", "networkNumber", "mediaFieBasePath"];
    const changed = fields.some(
      (f) => String(trimValue(current[f]) || "") !== String(trimValue(initialValues[f]) || "")
    );
    return changed || (isEdit && !sameRules(BusinessRuleRef, initialRules));
  };

  /**
   * on Save with errors, move focus to the first invalid field (design)
   */
  const focusFirstInvalid = async () => {
    const formErrors = await validateForm();
    const order = [
      ["networkName", networkNameInput],
      ["networkNumber", networkNumberInput],
      ["mediaFieBasePath", mediaFieBasePathInput],
    ];
    const first = order.find(([name]) => formErrors[name]);
    if (first && first[1].current && first[1].current.focus) {
      first[1].current.focus();
    }
  };

  const onFormSubmit = (e) => {
    handleSubmit(e);
    focusFirstInvalid();
  };

  // editing a field clears its server error and the form-level message (design)
  const fieldProps = (name) => {
    const fp = getFieldProps(name);
    return {
      ...fp,
      onChange: (e) => {
        if (serverErrors[name]) {
          setServerErrors({ ...serverErrors, [name]: "" });
        }
        setFormTopError("");
        fp.onChange(e);
      },
    };
  };

  // inline error: server error first, else the rule error once the field was left or Save clicked
  const fieldError = (name) =>
    serverErrors[name] || (touched[name] && errors[name] ? errors[name] : "");

  /**
   * cancel form btn and ×: ask to save when something changed, else close
   */
  const onFormCancelBtn = () => {
    if (saving) return;
    if (hasChanges()) {
      props.onClose();
    } else {
      props.handleModalClose();
    }
  };

  /**
   * function to get Ref Business Rules
   * @param {*passed list value} list
   */
  const getRefBusinessRules = (list) => {
    let rulesList = list
      // eslint-disable-next-line array-callback-return
      .map((item) => {
        if (item.value === true) {
          return item.name;
        }
      })
      .filter((x) => x !== undefined);
    setBusinessRuleRef(rulesList.length > 0 ? rulesList : ["Fill", "Bnus", "Paid"]);
  };

  /**
   * style object for edit zone dialog box
   */
  const useStyles = makeStyles({
    dialog: {
      position: "absolute",
      backgroundColor: "none",
      background: "none",
      left: 320,
      top: "45%",
    },
  });

  const classes = useStyles();

  // New Network sits in the modal's bordered panel; inline Edit uses the row's own frame
  const Panel = isEdit ? React.Fragment : FormPanel;

  return (
    <Box>
      {!isEdit && (
        <ModalHeader
          title="New Network"
          titleId="network-form-title"
          onClose={onFormCancelBtn}
          disabled={saving}
        />
      )}
      <form className="baseForm" onSubmit={onFormSubmit} noValidate>
        <Panel>
          {/* while saving, fields stay visible but can't be edited (design) */}
          <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
              {/* design grid: Name / Number / Parent Zone, then Media URL and Business Rules full width */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0,1.4fr) minmax(0,.8fr) minmax(0,1fr)",
                  gap: "24px",
                  mb: "22px",
                }}
              >
                <Box>
                  <FormFieldBlock
                    label="Network Name"
                    fieldId={isEdit ? `networkName-${updateNetworkData.mznlId}` : "networkName"}
                    required
                    value={values.networkName}
                    max={NAME_MAX}
                    showCounter
                    error={fieldError("networkName")}
                  >
                    <InputFormField
                      {...fieldProps("networkName")}
                      fieldType="text"
                      fieldName={isEdit ? `networkName-${updateNetworkData.mznlId}` : "networkName"}
                      maxLength={NAME_MAX}
                      aria-invalid={!!fieldError("networkName")}
                      errors={errors}
                      touched={touched}
                      getFocus={props.hightLightFields}
                      modalClose={props.confirmModalClose}
                      refName={networkNameInput}
                    />
                  </FormFieldBlock>
                </Box>
                <Box>
                  <FormFieldBlock
                    label="Network Number"
                    fieldId={isEdit ? `networkNumber-${updateNetworkData.mznlId}` : "networkNumber"}
                    required
                    value={values.networkNumber}
                    max={NUMBER_MAX}
                    showCounter={!isEdit}
                    locked={isEdit}
                    helper={isEdit ? DESIGN_TEXT.numberLocked : ""}
                    error={fieldError("networkNumber")}
                  >
                    <InputFormField
                      {...fieldProps("networkNumber")}
                      fieldType="text"
                      fieldName={isEdit ? `networkNumber-${updateNetworkData.mznlId}` : "networkNumber"}
                      maxLength={NUMBER_MAX}
                      disabled={isEdit}
                      title={isEdit ? DESIGN_TEXT.numberLocked : undefined}
                      aria-invalid={!!fieldError("networkNumber")}
                      errors={errors}
                      touched={touched}
                      getFocus={props.hightLightFields}
                      modalClose={props.confirmModalClose}
                      refName={networkNumberInput}
                    />
                  </FormFieldBlock>
                </Box>
                {parentZoneLabel && (
                  <Box>
                    <FormFieldBlock
                      label="Parent Zone"
                      fieldId={isEdit ? `parentZone-${updateNetworkData.mznlId}` : "parentZone"}
                      required
                      helper={isEdit ? "" : "Created within the current Zone."}
                    >
                      <input
                        id={isEdit ? `parentZone-${updateNetworkData.mznlId}` : "parentZone"}
                        type="text"
                        value={parentZoneLabel}
                        disabled
                        readOnly
                      />
                    </FormFieldBlock>
                  </Box>
                )}
              </Box>
              <Box sx={{ mb: "22px" }}>
                <FormFieldBlock
                  label="Media URL"
                  fieldId={isEdit ? `mediaFieBasePath-${updateNetworkData.mznlId}` : "mediaFieBasePath"}
                  required
                  error={fieldError("mediaFieBasePath")}
                >
                  <InputFormField
                    {...fieldProps("mediaFieBasePath")}
                    fieldType="text"
                    fieldName={
                      isEdit ? `mediaFieBasePath-${updateNetworkData.mznlId}` : "mediaFieBasePath"
                    }
                    maxLength={URL_MAX}
                    aria-invalid={!!fieldError("mediaFieBasePath")}
                    errors={errors}
                    touched={touched}
                    getFocus={props.hightLightFields}
                    modalClose={props.confirmModalClose}
                    refName={mediaFieBasePathInput}
                  />
                </FormFieldBlock>
              </Box>
              <Box sx={{ mb: "22px" }}>
                <FormFieldBlock label="Business Rules" fieldId="businessRule">
                  <BusinessRuleCheckbox
                    {...getFieldProps("businessRule")}
                    businessRule={initialValues.businessRule}
                    getRefBusinessRules={getRefBusinessRules}
                    fieldType="text"
                    fieldName="businessRule"
                    errors={errors}
                    touched={touched}
                    getFocus={props.hightLightFields}
                    modalClose={props.confirmModalClose}
                    refName={businessRuleInput}
                  />
                </FormFieldBlock>
              </Box>
          </fieldset>
        </Panel>
        <FormActions
          saving={saving}
          saveLabel={isEdit ? "SAVE" : "Save"}
          savingLabel={isEdit ? "SAVING…" : "Saving…"}
          cancelLabel={isEdit ? "CANCEL" : "Cancel"}
          onCancel={onFormCancelBtn}
          message={formTopError}
        />
        <Dialog
          className="confirmationDialogBox"
          open={props.dialogOpen}
          onClose={props.onModalClose}
          aria-labelledby="draggable-dialog-title"
          classes={{
            paper: classes.dialog,
          }}
        >
          <DialogContent>
            <div className="confirmationText">
              Do you want to save the changed before closing?
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setErrors({});
                props.handleModalClose();
              }}
              variant="contained"
              className="cancelConfirmBtn"
            >
              No
            </Button>
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              onClick={handleSubmit}
            >
              {props.dialogLoader && (
                <CircularProgress
                  size={15}
                  color="info"
                  className="saveLoaderZone"
                />
              )}{" "}
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          className="confirmationDialogBox"
          open={NoChange}
          aria-labelledby="draggable-dialog-title"
          classes={{
            paper: classes.dialog,
          }}
        >
          <DialogContent>
            <div className="confirmationText">No changes to save</div>
          </DialogContent>
        </Dialog>
      </form>
      {/* </Card> */}
    </Box>
  );
};

AddEditNetwork.propTypes = {
  onClose: PropTypes.func,
  zone: PropTypes.object,
};

export default AddEditNetwork;
